import set from 'set-value';
import { nextTick } from 'vue';

export class DiscardedTaskError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DiscardedTaskError';
  }
}

type AsyncFunction<T, R> = (...args: T[]) => Promise<R>;

/** A queued task function that also exposes a `cancel()` to drop pending/in-flight work. */
export type LatestTask<T, R> = ((...args: T[]) => Promise<R>) & {
  cancel: () => void;
};

/**
 * Optimizes calls for expensive asynchronous functions when you are only interested in the latest result.
 *
 * Usage:
 * ```
 * // An expensive async function
 * const process = async (value: number) => {
 *  console.log('Processing:', value);
 *  await new Promise(r => setTimeout(r, 1000));
 *  return `SUCCESS!!! ${value}`;
 * };
 *
 * const queueProcess = useQueue(process);
 *
 * queueProcess(1).catch(e => console.log(`Discarded: 1`))
 * queueProcess(2).catch(e => console.log(`Discarded: 2`))
 * queueProcess(3);
 *
 * // Output:
 * // Processing: 1
 * // Discarded: 2 (discarded the task)
 * // Processing: 3
 * // Discarded: 1 (discarded the result)
 * // SUCCESS!!! 3
 * ```
 *
 *  Returns a method with the same signature as the original expensive function that adds tasks to a queue. For each new call added while others are processing or pending, the older tasks are discarded and throw a custom DiscardedTaskError. Only the last call will return a result.
 *
 */
export function useLatestTask<T, R>(fn: AsyncFunction<T, R>): LatestTask<T, R> {
  let processing = false;
  let id = 0;
  // Bumped by cancel(); lets us discard the result of an in-flight task.
  let generation = 0;

  const queue: {
    id: number;
    args: T[];
    reject: (reason?: any) => void;
    resolve: (value: R | PromiseLike<R>) => void;
  }[] = [];

  const processQueue = async () => {
    if (queue.length === 0) {
      processing = false;
      return;
    }

    processing = true;

    // short wait for more tasks to be added
    await new Promise(r => setTimeout(r, 20));

    if (queue.length > 1) {
      queue.slice(0, -1).forEach(({ reject }) => {
        reject(new DiscardedTaskError('Discarded task'));
      });
    }

    let { args, resolve, reject, id } = queue[queue.length - 1]!;
    const startGeneration = generation;

    try {
      const result = await fn(...args);

      // have more tasks been added, or have we been cancelled, in the meantime?
      const outdated =
        queue.some(({ id: _id }) => id < _id) || generation !== startGeneration;

      if (outdated) {
        throw new DiscardedTaskError('Discarded result');
      }

      // this is the latest result, resolve and clear the queue
      resolve(result);
      queue.length = 0;
    } catch (e) {
      reject(e);
    } finally {
      processQueue();
    }
  };

  const addToQueue = ((...args: T[]) =>
    new Promise<R>((resolve, reject) => {
      id++;
      queue.push({ args, resolve, reject, id });
      if (!processing) processQueue();
    })) as LatestTask<T, R>;

  // Drop any queued tasks and disown the result of an in-flight one. A request
  // already on the wire still completes server-side, but its result is ignored.
  addToQueue.cancel = () => {
    generation++;
    queue.forEach(({ reject }) => {
      reject(new DiscardedTaskError('Cancelled task'));
    });
    queue.length = 0;
  };

  return addToQueue;
}

export default useLatestTask;
